import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useCooperativeId } from 'hooks';
import { useFormik } from 'formik';
import type {
  Document,
  DocumentsRes,
  EntityRef,
  InitTodoForm,
  Option,
  TaskExecutor,
  TaskTodo,
  TaskTodoCreateOptions,
} from 'models';
import { prepareModel, Warning } from './helpers';
import _isEqual from 'lodash/isEqual';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _unescape from 'lodash/unescape';
import services from 'services';

import { MAX_TODO_DESCRIPTION_LENGTH } from 'shared/constants';

import { Modal, TodoEditFormDetails } from 'components';

function getStatusListByValue(option: Option | undefined | null) {
  if (!option) {
    return [];
  }

  switch (option.Value) {
    case 2:
      return [{ Label: 'Työn alla', Value: 4 }];
    case 3:
      return [{ Label: 'Työn alla', Value: 4 }];
    case 4:
      return [{ Label: 'Työn alla', Value: 4 }];
    default:
      return [];
  }
}

interface Props {
  handleClear?: () => void;
  handleClose: (upload?: boolean) => void;
  taskId: string | null;
  handleSetInitTodoForm: (form: InitTodoForm) => void;
}

interface LocaleState {
  documents: Document[];
  documentsIsTouched: boolean;
  options: TaskTodoCreateOptions | null;
  loading: boolean;
  error: boolean;
  warningState: {
    open: boolean;
    type: 'cancel' | 'complete' | null;
  };
  confirmationWarning: boolean;
  upload: boolean;
  saveAndCopyUpload: boolean;
}

const TodoEditForm: React.FC<Props> = ({
  taskId,
  handleClear,
  handleClose,
  handleSetInitTodoForm,
}) => {
  const initialState = useRef<TaskTodo>({} as TaskTodo);
  const coopId = useCooperativeId();
  const [state, setState] = useState<LocaleState>({
    documents: [],
    documentsIsTouched: false,
    options: null,
    warningState: {
      open: false,
      type: null,
    },
    confirmationWarning: false,
    loading: false,
    upload: false,
    error: false,
    saveAndCopyUpload: false,
  });
  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {} as TaskTodo,
    validate: (values) => {
      let errors = {};

      if (!values.Subject) {
        _set(errors, 'Subject', Warning.Required);
        return errors;
      }

      if (values.Subject.length > 200) {
        _set(errors, 'Subject', Warning.SymbolsLimit);
        return errors;
      }

      if (
        values.Description &&
        values.Description.length > MAX_TODO_DESCRIPTION_LENGTH
      ) {
        _set(errors, 'Description', Warning.DescriptionSymbolsLimit);
      }

      if (
        values.ResolutionDescription &&
        values.ResolutionDescription.length > MAX_TODO_DESCRIPTION_LENGTH
      ) {
        _set(errors, 'ResolutionDescription', Warning.DescriptionSymbolsLimit);
      }

      if (!values.DueDate) {
        _set(errors, 'DueDate', Warning.Required);
        return errors;
      }

      if (!values.Executor) {
        _set(errors, 'Executor', Warning.Required);
        return errors;
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const body = prepareModel(values);
        return (
          await services.put(
            `/api/cooperatives/${coopId}/tasks/todo/${taskId}`,
            body
          )
        ).data.Data;
      } catch (err) {
        console.error(err);

        setState((prevState) => ({ ...prevState, upload: false }));
      }
    },
  });

  useEffect(() => {
    if (coopId && taskId) {
      (async function () {
        try {
          setState((prevState) => ({ ...prevState, loading: true }));

          let documents: Document[] = [];
          const options = await fetchOptions(coopId);
          const executors = options.Executor.map((executor) => {
            const name = `${executor.Name} ${
              executor.Roles.length ? `(${executor.Roles.join(', ')})` : ''
            }`;

            return {
              ...executor,
              Name: name,
            };
          });
          const todoTask = await fetchTodoTask(coopId, taskId, executors);

          if (todoTask.HasFiles) {
            const documentsRes = await services.get<{ Data: DocumentsRes[] }>(
              `/api/user/cooperatives/${coopId}/documents/cached/task/${taskId}`
            );

            if (Array.isArray(documentsRes.data.Data[0].Documents)) {
              documents = documentsRes.data.Data[0].Documents;
            }
          }

          setState((prevState) => ({
            ...prevState,
            documents,
            options: {
              ...options,
              Executor: executors,
              Status: getStatusListByValue(todoTask.Status),
            },
            loading: false,
          }));
        } catch (err) {
          console.error(err);
          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: true,
          }));
        }
      })();
    }
  }, [coopId, taskId]);

  const compareState = () => _isEqual(initialState.current, formik.values);

  const fetchOptions = async (coopId: string) => {
    const options: TaskTodoCreateOptions = (
      await services.get(
        `/api/cooperatives/${coopId}/tasks/todo/update/options`
      )
    ).data.Data;

    return options;
  };

  const fetchTodoTask = async (
    coopId: string,
    taskId: string,
    executors: TaskExecutor[]
  ) => {
    const todoTask: TaskTodo = (
      await services.get(`/api/cooperatives/${coopId}/tasks/todo/${taskId}`)
    ).data.Data;

    const currentExecutor =
      executors.find((executor) => executor.Id === todoTask.Executor?.Id) ??
      null;

    const modifiedTask: TaskTodo = {
      ...todoTask,
      Description: todoTask.Description && _unescape(todoTask.Description),
      ResolutionDescription:
        todoTask.ResolutionDescription &&
        _unescape(todoTask.ResolutionDescription),
      Subject: todoTask.Subject && _unescape(todoTask.Subject),
      Executor: currentExecutor,
    };

    formik.setValues(modifiedTask);
    initialState.current = modifiedTask;

    return modifiedTask;
  };

  const closeModal = useCallback(() => {
    const equal = compareState();

    if (equal) {
      handleClose(state.documentsIsTouched);
    } else {
      setState((prevState) => ({ ...prevState, confirmationWarning: true }));
    }

    /* eslint-disable-next-line */
  }, [compareState, state.documentsIsTouched]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    formik.setFieldValue(name, value);

    /* eslint-disable-next-line */
  }, []);

  const handleChangeSelectValue = useCallback(
    (value: Option | EntityRef, name?: string) => {
      if (name) {
        formik.setFieldValue(name, value);
      }

      /* eslint-disable-next-line */
    },
    []
  );

  const handleChangeTextArea = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      formik.setFieldValue(name, value);

      /* eslint-disable-next-line */
    },
    []
  );

  const handleChangeDate = useCallback((date: Date | null, name?: string) => {
    if (name) {
      let transformedDate = date ? date.toString() : null;

      formik.setFieldValue(name, transformedDate);
    }

    /* eslint-disable-next-line */
  }, []);

  const handleCompleteTask = useCallback(async () => {
    if (formik.values.HasSubTasks) {
      setState((prevState) => ({
        ...prevState,
        warningState: {
          type: 'complete',
          open: true,
        },
      }));
      return;
    }

    setState((prevState) => ({ ...prevState, upload: true }));

    try {
      const equal = compareState();

      if (!equal) {
        await formik.submitForm();
      }

      const flag = (
        await services.put(
          `/api/cooperatives/${coopId}/tasks/todo/${taskId}/complete`
        )
      ).data.Data;

      if (flag) {
        handleClose(true);
      }
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, upload: false }));
    }

    /* eslint-disable-next-line */
  }, [formik.values]);

  const handleCancelTask = useCallback(async () => {
    if (formik.values.HasSubTasks) {
      setState((prevState) => ({
        ...prevState,
        warningState: {
          type: 'cancel',
          open: true,
        },
      }));
      return;
    }

    setState((prevState) => ({ ...prevState, upload: true }));

    try {
      const equal = compareState();

      if (!equal) {
        await formik.submitForm();
      }

      const flag = (
        await services.put(
          `/api/cooperatives/${coopId}/tasks/todo/${taskId}/cancel`
        )
      ).data.Data;

      if (flag) {
        handleClose(true);
      }
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, upload: false }));
    }

    /* eslint-disable-next-line */
  }, [formik.values]);

  const handleReopenTask = useCallback(async () => {
    try {
      setState((prevState) => ({ ...prevState, upload: true }));

      const flag = (
        await services.put(
          `/api/cooperatives/${coopId}/tasks/todo/${taskId}/reopen`
        )
      ).data.Data;

      if (flag) {
        setState((prevState) => ({ ...prevState, upload: false }));
        handleClose(true);
      }
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, upload: false }));
    }

    /* eslint-disable-next-line */
  }, []);

  const handleCloseSubtaskWarning = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      warningState: {
        ...prevState.warningState,
        open: false,
      },
    }));
  }, []);

  const handleInitWarningType = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      warningState: {
        ...prevState.warningState,
        type: null,
      },
    }));
  }, []);

  const closeWithoutSaving = () => {
    setState((prevState) => ({ ...prevState, confirmationWarning: false }));
    handleClose(state.documentsIsTouched);
  };

  const closeWithSaving = useCallback(async () => {
    let errors = await formik.validateForm();

    const isValid = !_size(errors);

    if (isValid) {
      setState((prevState) => ({
        ...prevState,
        upload: true,
        confirmationWarning: false,
      }));

      const res = await formik.submitForm();

      if (res) {
        handleClose(true);
      }
    }

    /* eslint-disable-next-line */
  }, [formik.values]);

  const handleCloseTask = useCallback(() => {
    const equal = compareState();
    if (equal) {
      handleClose(state.documentsIsTouched);
      return;
    }

    setState((prevState) => ({ ...prevState, confirmationWarning: true }));
  }, [formik.values, state.documentsIsTouched]);

  const handleInitErrors = useCallback(() => {
    formik.setErrors({});

    /* eslint-disable-next-line */
  }, []);

  const handleSaveAndCopy = useCallback(async () => {
    let errors = await formik.validateForm();

    const isValid = !_size(errors);

    if (isValid) {
      setState((prevState) => ({
        ...prevState,
        saveAndCopyUpload: true,
      }));

      const res = await formik.submitForm();

      if (res) {
        handleSetInitTodoForm(formik.values);
      } else {
        setState((prevState) => ({
          ...prevState,
          saveAndCopyUpload: false,
        }));
      }
    }
  }, [handleSetInitTodoForm, formik]);

  const handleChangeDescription = useCallback(
    (value) => {
      formik.setFieldValue('Description', value);
    },
    [formik]
  );

  const handleChangeResolutionDescription = useCallback(
    (value) => {
      formik.setFieldValue('ResolutionDescription', value);
    },
    [formik]
  );

  const touchDocuments = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      documentsIsTouched: true,
    }));
  }, []);

  return (
    <Modal
      open={Boolean(taskId)}
      maxWidth="lg"
      fullWidth
      handleClose={closeModal}
      TransitionProps={{
        onExited: handleClear,
      }}
    >
      <TodoEditFormDetails
        {...state}
        taskId={taskId}
        touchDocuments={touchDocuments}
        handleInitWarningType={handleInitWarningType}
        handleSaveAndCopy={handleSaveAndCopy}
        closeWithoutSaving={closeWithoutSaving}
        closeWithSaving={closeWithSaving}
        handleInitErrors={handleInitErrors}
        handleCloseTask={handleCloseTask}
        handleCancelTask={handleCancelTask}
        handleCompleteTask={handleCompleteTask}
        handleCloseSubtaskWarning={handleCloseSubtaskWarning}
        handleChangeDate={handleChangeDate}
        handleChangeInput={handleChangeInput}
        handleChangeSelect={handleChangeSelectValue}
        handleChangeTextArea={handleChangeTextArea}
        handleReopenTask={handleReopenTask}
        values={formik.values}
        validationErrors={formik.errors}
        onChangeDescription={handleChangeDescription}
        onChangeResolutionDescription={handleChangeResolutionDescription}
      />
    </Modal>
  );
};

export default TodoEditForm;
